import {
  ArrowUpTrayIcon,
  CalculatorIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  CurrencyYenIcon,
  EnvelopeIcon,
  EyeDropperIcon,
  FolderIcon,
  HomeIcon,
  ScaleIcon,
  TableCellsIcon,
  UsersIcon,
} from '@heroicons/react/24/solid';
import { useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { FeatureFlagType, FeatureStatus } from '@/entities/session/model/featureFlag';
import type { ReleaseFlagType } from '@/entities/session/model/releaseFlag';
import { useFeatureFlag } from '@/entities/session/model/useFeatureFlag';
import { useIsAdmin } from '@/entities/session/model/useIsAdmin';
import { useReleaseFlag } from '@/entities/session/model/useReleaseFlag';
import type { NavMenuItem } from '@/shared/ui/layout/nav-menu-items/NavMenuItems';

// ナビゲーション表示をゲートするリリースフラグのみを列挙する。
// メニューの出し分けに使うフラグだけをここに足せばよく、
// 非ナビ用途のフラグ（例: ドロップダウン選択肢の出し分け）ではこのファイルを触らなくて済む。
type NavGatedReleaseFlagType = Extract<
  ReleaseFlagType,
  'settlement_statistics_converter' | 'system_feedback'
>;

// ナビ表示をゲートする機能フラグ（テナント単位）のみを列挙する。
type NavGatedFeatureFlagType = Extract<FeatureFlagType, 'expenditure_comparison'>;

type ItemDef = NavMenuItem & {
  releaseFlagType?: NavGatedReleaseFlagType;
  // 機能フラグで 3 段階制御する項目に付与する。
  // visible: 通常表示 / disabled: 非活性（グレーアウト・遷移不可） / hidden: メニューから除外
  featureFlagType?: NavGatedFeatureFlagType;
};

// ITA-2515: `useMatchRoute()` は安定参照の関数 + 定数引数の組み合わせのため、
// React Compiler が結果をキャッシュしてしまい Link 遷移時に isActive が更新されない。
// pathname を直接読み取り fuzzy マッチを行うことで Compiler に依存関係を明示する。
const isFuzzyMatch = (pathname: string, basePath: string): boolean =>
  pathname === basePath || pathname.startsWith(`${basePath}/`);

export const useRouteMenu = (): NavMenuItem[] => {
  const { t } = useTranslation();
  const pathname = useLocation({ select: (location) => location.pathname });
  const isAdmin = useIsAdmin();

  // ナビ表示をゲートするリリースフラグの有効状態を取得（Record で網羅性を強制）
  const releaseFlagEnabledMap: Record<NavGatedReleaseFlagType, boolean> = {
    settlement_statistics_converter: useReleaseFlag('settlement_statistics_converter'),
    system_feedback: useReleaseFlag('system_feedback'),
  };

  // ナビ表示をゲートする機能フラグの状態を取得（Record で網羅性を強制）
  const featureFlagStatusMap: Record<NavGatedFeatureFlagType, FeatureStatus> = {
    expenditure_comparison: useFeatureFlag('expenditure_comparison'),
  };

  // pathname ベースで fuzzy マッチを判定（前方一致 + 区切り `/` チェック）
  const isHomePage = isFuzzyMatch(pathname, '/home');
  const isAiAssistantPage = isFuzzyMatch(pathname, '/ai-chat');
  const isAiMatchingResultsPage = isFuzzyMatch(pathname, '/projects/ai-matching-results');
  // 「事業」自体のアクティブ判定は /projects 配下全般だが、子の AIマッチング結果ページに
  // 居るときは親ハイライトを外して子だけハイライトされるようにする
  const isProjectsPage = isFuzzyMatch(pathname, '/projects') && !isAiMatchingResultsPage;
  const isAssessmentPage = isFuzzyMatch(pathname, '/assessment');
  const isSettlementStatisticsConverterPage = isFuzzyMatch(
    pathname,
    '/settlement-statistics-converter',
  );
  const isDatConversionPage = isFuzzyMatch(pathname, '/dat-conversion');
  const isDataImportPage = isFuzzyMatch(pathname, '/data-import');
  const isJobPage = isFuzzyMatch(pathname, '/job');
  const isPlanUsagePage = isFuzzyMatch(pathname, '/plan-usage');
  const isMasterPage = isFuzzyMatch(pathname, '/master');
  const isUsersPage = isFuzzyMatch(pathname, '/users');
  const isInquiryPage = isFuzzyMatch(pathname, '/inquiry');

  const allItems: ItemDef[] = [
    {
      key: 'home',
      icon: HomeIcon,
      label: t('home.navigation.title'),
      isActive: isHomePage,
      to: '/home',
      hasBottomSeparator: true,
    },
    {
      key: 'ai-assistant',
      icon: ChatBubbleLeftRightIcon,
      label: t('ai.ai-assistant.navigation-title'),
      isActive: isAiAssistantPage,
      to: '/ai-chat',
      hasBottomSeparator: true,
    },
    {
      key: 'projects',
      icon: FolderIcon,
      label: t('project.navigation.title'),
      isActive: isProjectsPage,
      to: '/projects',
      children: [
        {
          key: 'ai-matching-results',
          label: t('ai-matching-results.navigation.title'),
          isActive: isAiMatchingResultsPage,
          to: '/projects/ai-matching-results',
        },
      ],
    },
    {
      key: 'assessment',
      icon: ScaleIcon,
      label: t('assessment.navigation.title'),
      isActive: isAssessmentPage,
      to: '/assessment',
      hasBottomSeparator: true,
      featureFlagType: 'expenditure_comparison',
    },
    {
      key: 'settlement-statistics-converter',
      icon: CalculatorIcon,
      label: t('settlement-statistics-converter.navigation.code-assignment'),
      isActive: isSettlementStatisticsConverterPage,
      to: '/settlement-statistics-converter',
      releaseFlagType: 'settlement_statistics_converter',
    },
    {
      key: 'dat-conversion',
      icon: EyeDropperIcon,
      label: t('dat-conversion.navigation.title'),
      isActive: isDatConversionPage,
      to: '/dat-conversion',
      hasBottomSeparator: true,
      releaseFlagType: 'settlement_statistics_converter',
    },
    {
      key: 'data-import',
      icon: ArrowUpTrayIcon,
      label: t('data-import.navigation.title'),
      isActive: isDataImportPage,
      to: '/data-import',
    },
    {
      key: 'job',
      icon: ClipboardDocumentListIcon,
      label: t('job.navigation.title'),
      isActive: isJobPage,
      to: '/job',
      hasBottomSeparator: true,
    },
    {
      key: 'master',
      sectionHeader: t('master-management.section-header'),
      icon: TableCellsIcon,
      label: t('master-management.navigation.title'),
      isActive: isMasterPage,
      to: '/master/accounting-types',
    },
    // ユーザー管理・プラン＆使用量は admin のみ表示（セッション読み込み中の null は非表示）
    ...(isAdmin === true
      ? [
          {
            key: 'users',
            icon: UsersIcon,
            label: t('user-management.navigation.title'),
            isActive: isUsersPage,
            to: '/users' as const,
          } satisfies ItemDef,
          {
            key: 'plan-usage',
            icon: CurrencyYenIcon,
            label: t('plan-usage.navigation.title'),
            isActive: isPlanUsagePage,
            to: '/plan-usage' as const,
          } satisfies ItemDef,
        ]
      : []),
    // 問い合わせ（最下部、system_feedback リリースフラグでゲート）
    {
      key: 'inquiry',
      icon: EnvelopeIcon,
      label: t('inquiry.navigation.title'),
      isActive: isInquiryPage,
      to: '/inquiry',
      releaseFlagType: 'system_feedback',
    },
  ];

  // リリースフラグが false の項目、機能フラグが hidden の項目はメニューから除外する。
  // 機能フラグが disabled の項目は非活性（グレーアウト・遷移不可）でメニューに残す。
  return allItems
    .filter((item) => !item.releaseFlagType || releaseFlagEnabledMap[item.releaseFlagType])
    .filter(
      (item) => !item.featureFlagType || featureFlagStatusMap[item.featureFlagType] !== 'hidden',
    )
    .map(({ releaseFlagType: _releaseFlagType, featureFlagType, ...rest }) => ({
      ...rest,
      isDisabled:
        rest.isDisabled ||
        (featureFlagType ? featureFlagStatusMap[featureFlagType] === 'disabled' : false),
    }));
};
